import { env } from '@/config/env';

/**
 * The store's wall clock.
 *
 * Most of the app reads timestamps in the browser's zone, which is right for
 * an order or a booking — the reader wants to know when it happened *to them*.
 * A few pages are about the store's own day instead: a shift that started at
 * 8am should read 8am to whoever opens the timesheet, and a drawer counted at
 * close belongs to the day the till was standing in, not the day the manager
 * is sitting in. Those pages use these.
 *
 * The offset comes from `VITE_TIMEZONE_OFFSET`, which is what the original
 * added and subtracted by hand at every call site.
 */

const OFFSET_MS = env.timezoneOffset * 3_600_000;

/** An API instant as the store's wall clock. */
export function toStoreTime(value: string | null | undefined): Date | null {
  if (!value) return null;
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
  const parsed = new Date(hasZone ? value : `${value}Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getTime() + OFFSET_MS);
}

/** The store's wall clock, for a `datetime-local` input. */
export function toInputValue(value: string | null | undefined): string {
  const date = toStoreTime(value);
  return date ? date.toISOString().slice(0, 16) : '';
}

/**
 * A `datetime-local` value back into what the API stores.
 *
 * Sent without a zone marker, which is the shape these endpoints take.
 */
export function fromInputValue(value: string): string | null {
  if (!value) return null;
  const local = new Date(`${value}:00.000Z`);
  return new Date(local.getTime() - OFFSET_MS)
    .toISOString()
    .replace('Z', '')
    .slice(0, 23);
}

/**
 * A store wall-clock instant as the zoneless string the API stores.
 *
 * `date` must carry the store's wall clock in its **UTC** fields — the shape
 * every builder here produces. Reading local fields instead applies the
 * browser's own offset on top of this one, which is the double shift that
 * moved every filtered range by four hours.
 */
export function toApiInstant(date: Date): string {
  return `${new Date(date.getTime() - OFFSET_MS).toISOString().slice(0, 19)}.000`;
}

/** A picked `yyyy-MM-dd` as an API instant. `endOfDay` takes the day's last minute. */
export function toApiDate(value: string, endOfDay = false): string {
  return toApiInstant(
    new Date(`${value}T${endOfDay ? '23:59' : '00:00'}:00.000Z`)
  );
}

/** A span of calendar days at the store, as a query range. */
export function dayBounds(start: string, end: string) {
  const from = new Date(`${start}T00:00:00.000Z`);
  // The range ends a minute before the following midnight.
  const to = new Date(`${end}T00:00:00.000Z`);
  to.setUTCDate(to.getUTCDate() + 1);
  to.setUTCMinutes(to.getUTCMinutes() - 1);

  return { startDate: toApiInstant(from), endDate: toApiInstant(to) };
}

/** The first and last day of a `yyyy-MM`, as input-ready dates. */
export function monthBounds(month: string) {
  const [year, index] = month.split('-').map(Number);
  const last = new Date(Date.UTC(year, index, 0)).getUTCDate();
  return { start: `${month}-01`, end: `${month}-${String(last).padStart(2, '0')}` };
}

/** A timestamp read in the store's clock rather than the reader's. */
export function formatStoreTime(
  value: string | null | undefined,
  dateOnly = false
) {
  const date = toStoreTime(value);
  if (!date) return '—';
  const iso = date.toISOString();
  return dateOnly ? iso.slice(0, 10) : `${iso.slice(0, 10)} ${iso.slice(11, 16)}`;
}

/** Today at the store, as an input-ready date. */
export function storeToday() {
  const now = new Date(Date.now() + OFFSET_MS);
  return now.toISOString().slice(0, 10);
}
